import { ChevronDown, ChevronRight, Pencil, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type SubParam = {
	id: string;
	name: string;
	percentage: number;
};

type ScreeningParam = {
	id: string;
	name: string;
	weight: number;
	subParams: SubParam[];
};

type ConfigVersion = {
	id: string;
	versionId: string;
	status: 'active' | 'inactive';
	createdAt: string;
	createdBy: string;
	params: ScreeningParam[];
};

const DEFAULT_PARAMS: ScreeningParam[] = [
	{
		id: 'param_name',
		name: 'Name',
		weight: 30,
		subParams: [
			{ id: 'sp_fname', name: 'First Name', percentage: 50 },
			{ id: 'sp_lname', name: 'Last Name', percentage: 50 },
		],
	},
	{
		id: 'param_dob',
		name: 'Date of Birth',
		weight: 20,
		subParams: [],
	},
	{
		id: 'param_nat',
		name: 'Nationality',
		weight: 15,
		subParams: [],
	},
	{
		id: 'param_addr',
		name: 'Address',
		weight: 15,
		subParams: [
			{ id: 'sp_country', name: 'Country', percentage: 40 },
			{ id: 'sp_city', name: 'City', percentage: 30 },
			{ id: 'sp_street', name: 'Street', percentage: 30 },
		],
	},
	{
		id: 'param_id',
		name: 'ID Documents',
		weight: 10,
		subParams: [
			{ id: 'sp_passport', name: 'Passport', percentage: 50 },
			{ id: 'sp_natid', name: 'National ID', percentage: 50 },
		],
	},
	{
		id: 'param_media',
		name: 'Adverse Media',
		weight: 10,
		subParams: [],
	},
];

const MOCK_HISTORY: ConfigVersion[] = [
	{
		id: 'cfg_v3',
		versionId: 'cfg_v3',
		status: 'active',
		createdAt: 'May 20, 2025',
		createdBy: 'Alex Morgan',
		params: DEFAULT_PARAMS,
	},
	{
		id: 'cfg_v2',
		versionId: 'cfg_v2',
		status: 'inactive',
		createdAt: 'Apr 10, 2025',
		createdBy: 'Jordan Kim',
		params: DEFAULT_PARAMS.map((p) => ({ ...p, weight: p.weight })),
	},
	{
		id: 'cfg_v1',
		versionId: 'cfg_v1',
		status: 'inactive',
		createdAt: 'Feb 3, 2025',
		createdBy: 'Sam Rivera',
		params: DEFAULT_PARAMS.slice(0, 4),
	},
];

function WeightSummaryBar({ params }: { params: ScreeningParam[] }) {
	const total = params.reduce((sum, p) => sum + p.weight, 0);
	const over = total > 100;
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex items-center justify-between text-xs text-[#737373]">
				<span>Total Weight Used</span>
				<span
					className={
						over ? 'font-semibold text-red-600' : 'font-semibold text-[#0a0a0a]'
					}
				>
					{total}%
				</span>
			</div>
			<Progress value={Math.min(total, 100)} className="h-2" />
			{over && (
				<p className="text-xs text-red-600">
					Total weight exceeds 100%. Please adjust.
				</p>
			)}
		</div>
	);
}

function ParameterCard({
	param,
	editMode,
	onChange,
}: {
	param: ScreeningParam;
	editMode: boolean;
	onChange: (updated: ScreeningParam) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	const hasSubParams = param.subParams.length > 0;

	return (
		<div className="overflow-hidden rounded-lg border border-[#e5e5e5] bg-white">
			<div className="flex items-center justify-between px-4 py-3">
				<div className="flex items-center gap-2">
					{hasSubParams ? (
						<button
							type="button"
							onClick={() => setExpanded((v) => !v)}
							className="text-[#737373] hover:text-[#0a0a0a]"
						>
							{expanded ? (
								<ChevronDown className="h-4 w-4" />
							) : (
								<ChevronRight className="h-4 w-4" />
							)}
						</button>
					) : (
						<span className="h-4 w-4" />
					)}
					<span className="text-sm font-medium text-[#0a0a0a]">
						{param.name}
					</span>
				</div>
				{editMode ? (
					<div className="flex items-center gap-1">
						<Input
							type="number"
							min={0}
							max={100}
							value={param.weight}
							className="h-7 w-16 text-center text-sm"
							onChange={(e) =>
								onChange({
									...param,
									weight: Math.max(0, Math.min(100, Number(e.target.value))),
								})
							}
						/>
						<span className="text-xs text-[#737373]">%</span>
					</div>
				) : (
					<Badge variant="secondary" className="text-xs">
						{param.weight}%
					</Badge>
				)}
			</div>
			{expanded && hasSubParams && (
				<div className="border-t border-[#e5e5e5] bg-[#fafafa] px-8 py-2">
					<div className="flex flex-col gap-1.5">
						{param.subParams.map((sp) => (
							<div
								key={sp.id}
								className="flex items-center justify-between py-1"
							>
								<span className="text-xs text-[#525252]">{sp.name}</span>
								<Badge variant="outline" className="text-xs">
									{sp.percentage}%
								</Badge>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function HistoryParamsView({ params }: { params: ScreeningParam[] }) {
	return (
		<Table>
			<TableHeader>
				<TableRow className="bg-[#fafafa]">
					<TableHead className="text-xs font-semibold text-[#737373]">
						Parameter
					</TableHead>
					<TableHead className="text-xs font-semibold text-[#737373]">
						Weight
					</TableHead>
					<TableHead className="text-xs font-semibold text-[#737373]">
						Sub-Parameters
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{params.map((p) => (
					<TableRow key={p.id}>
						<TableCell className="text-sm font-medium text-[#0a0a0a]">
							{p.name}
						</TableCell>
						<TableCell>
							<Badge variant="secondary" className="text-xs">
								{p.weight}%
							</Badge>
						</TableCell>
						<TableCell className="text-sm text-[#525252]">
							{p.subParams.length > 0
								? p.subParams
										.map((sp) => `${sp.name} (${sp.percentage}%)`)
										.join(', ')
								: '—'}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

export default function ScreeningConfigPage() {
	const [params, setParams] = useState<ScreeningParam[]>(DEFAULT_PARAMS);
	const [snapshot, setSnapshot] = useState<ScreeningParam[]>(DEFAULT_PARAMS);
	const [editMode, setEditMode] = useState(false);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<ConfigVersion | null>(
		null,
	);

	const handleParamChange = useCallback((updated: ScreeningParam) => {
		setParams((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
	}, []);

	const handleEnterEdit = () => {
		setSnapshot(params);
		setEditMode(true);
	};

	const handleCancel = () => {
		setParams(snapshot);
		setEditMode(false);
	};

	const handleSave = () => {
		setEditMode(false);
		toast.success('Configuration saved successfully');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Screening Configuration
				</h1>
				<p className="mt-0.5 text-sm text-[#737373]">
					Manage screening parameter weights and sub-parameter settings
				</p>
			</div>

			<div className="p-6">
				<Tabs defaultValue="active">
					<TabsList>
						<TabsTrigger value="active">Active Configuration</TabsTrigger>
						<TabsTrigger value="history">Configuration History</TabsTrigger>
					</TabsList>

					<TabsContent value="active" className="mt-6">
						<div className="overflow-hidden rounded-xl bg-white shadow-sm">
							<div className="flex items-start justify-between px-6 py-4">
								<div className="flex flex-col gap-1">
									<h3 className="text-base font-semibold text-[#0a0a0a]">
										Active Configuration
									</h3>
									<div className="flex flex-wrap items-center gap-4 text-xs text-[#737373]">
										<span>Version: cfg_v3</span>
										<span>Created: May 20, 2025</span>
										<span>By: Alex Morgan</span>
									</div>
								</div>
								<div className="flex items-center gap-2">
									{editMode ? (
										<>
											<Button
												variant="outline"
												size="sm"
												onClick={handleCancel}
											>
												<X className="mr-1 h-4 w-4" />
												Cancel
											</Button>
											<Button
												size="sm"
												className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
												onClick={handleSave}
											>
												Save
											</Button>
										</>
									) : (
										<Button
											variant="outline"
											size="sm"
											onClick={handleEnterEdit}
										>
											<Pencil className="mr-1 h-4 w-4" />
											Edit
										</Button>
									)}
								</div>
							</div>

							<div className="px-6 pb-4">
								<WeightSummaryBar params={params} />
							</div>

							<div className="grid grid-cols-2 gap-3 px-6 pb-6">
								{params.map((param) => (
									<ParameterCard
										key={param.id}
										param={param}
										editMode={editMode}
										onChange={handleParamChange}
									/>
								))}
							</div>
						</div>
					</TabsContent>

					<TabsContent value="history" className="mt-6">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#fafafa]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Config Version ID
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Status
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Created At
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Created By
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Actions
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{MOCK_HISTORY.map((v) => (
										<TableRow
											key={v.id}
											className="cursor-pointer hover:bg-[#fafafa]"
										>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												{v.versionId}
											</TableCell>
											<TableCell>
												{v.status === 'active' ? (
													<Badge className="border border-green-200 bg-green-50 text-green-700 hover:bg-green-50 text-xs">
														Active
													</Badge>
												) : (
													<Badge variant="secondary" className="text-xs">
														Inactive
													</Badge>
												)}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{v.createdAt}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{v.createdBy}
											</TableCell>
											<TableCell>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => {
														setSelectedVersion(v);
														setSheetOpen(true);
													}}
												>
													View
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>
				</Tabs>
			</div>

			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto">
					<SheetHeader>
						<SheetTitle>Configuration {selectedVersion?.versionId}</SheetTitle>
					</SheetHeader>
					{selectedVersion && (
						<div className="mt-4 flex flex-col gap-4">
							<div className="flex flex-wrap gap-4 text-xs text-[#737373]">
								<span>Created At: {selectedVersion.createdAt}</span>
								<span>Created By: {selectedVersion.createdBy}</span>
								<span>
									Status:{' '}
									<span
										className={
											selectedVersion.status === 'active'
												? 'text-green-600'
												: ''
										}
									>
										{selectedVersion.status === 'active'
											? 'Active'
											: 'Inactive'}
									</span>
								</span>
							</div>
							<div className="overflow-hidden rounded-lg border border-[#e5e5e5]">
								<HistoryParamsView params={selectedVersion.params} />
							</div>
						</div>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
